import {useMobileAppRedirect} from "@/api/userApi";
import RightArrowIcon from "@/assets/colorable/right_arrow.svg?react";
import PhoneIcon from "@/assets/dark_gradient/phone.svg?react";
import Button from "@/components/Common/Button";
import AuthPageHeader from "@/components/registration/AuthPageHeader";
import clsxm from "@/lib/clsxm";
import LoginPageTabMenu from "@/pages/login/LoginPageTabMenu";
import RegistrationBottomView from "@/pages/registerCompany/RegistrationBottomView";
import RegistrationTopView from "@/pages/registerCompany/RegistrationTopView";
import CompanyDetailsStep from "@/pages/registerCompany/steps/CompanyDetailsStep";
import CredentialsStep from "@/pages/registerCompany/steps/CredentialsStep";
import InterestSelectionStep from "@/pages/registerCompany/steps/InterestSelectionStep";
import useRegistrationStore from "@/store/registration";
import {createFileRoute} from "@tanstack/react-router";
import {useEffect} from "react";
import {useTranslation} from "react-i18next";
import {useEffectOnce, useMedia, useTitle} from "react-use";
import {z} from "zod";

const RegisterSearchSchema = z.object({
  join_ref: z.string().optional(),
  utm_medium: z.string().optional(),
  utm_source: z.string().optional(),
});

export const Route = createFileRoute("/_unauthenticated/register/")({
  component: Register,
  validateSearch: search => RegisterSearchSchema.parse(search),
  beforeLoad: () => {
    // Start the signup form from a clean slate before it mounts, so it never inherits
    // invite-sourced prefill. A partial clear in an effect runs too late
    // (RegistrationEmailField seeds its local state from the store on mount) and would
    // leave the multi-step state inconsistent, so reset the whole store here and keep
    // only the app-wide language selection.
    const store = useRegistrationStore.getState();
    const {languageCode} = store;
    store.reset();
    if (languageCode) {
      store.setValue("languageCode", languageCode);
    }
  },
});

function Register() {
  const {t} = useTranslation("common");
  const isTall = useMedia("(min-height: 1200px)");
  const navigate = Route.useNavigate();
  const step = useRegistrationStore(state => state.step);
  const setValue = useRegistrationStore(state => state.setValue);
  const {openAppRedirect} = useMobileAppRedirect();

  useTitle(t("registerCompany"));

  useEffectOnce(() => {
    if (window.location.origin.includes("refluenced.de")) {
      setValue("country", "Germany");
    }
  });
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  return (
    <div
      className={clsxm(
        "flex w-full flex-col items-center px-4 sm:px-20 lg:px-8 xl:px-32",
        isTall && step !== "interest_selection" ? "h-full justify-center gap-10" : "justify-start gap-8",
        step === "credentials" && "px-0 sm:px-0 lg:px-0 xl:px-0",
      )}>
      <div
        className={clsxm(
          "flex w-full flex-col",
          step === "credentials" && "px-4 sm:px-20 lg:px-8 xl:px-32",
          isTall && step !== "interest_selection" ? "gap-10" : "gap-4 sm:gap-8",
        )}>
        <LoginPageTabMenu />
        <AuthPageHeader title={t("register_brand_account_title")} />
        <RegistrationTopView />
      </div>
      {step === "credentials" && (
        <div className="flex w-full flex-col">
          <CredentialsStep className="px-4 pt-10 sm:px-20 lg:px-8 xl:px-32" />
          <div className="bg-primary-powder flex flex-col gap-5 pt-3 pb-10">
            <RegistrationBottomView />
            <div className="flex items-center justify-center gap-5 font-medium">
              <span className="text-neutral-black">{t("have_an_account_already")}</span>
              <button onClick={() => navigate({to: "/login"})}>
                <span className="text-primary-dark-solid">{t("log_in")}</span>
              </button>
            </div>
          </div>
        </div>
      )}
      {step === "company_details" && <CompanyDetailsStep />}
      {step === "interest_selection" && <InterestSelectionStep />}
      {step !== "credentials" && <RegistrationBottomView />}
      <div className="bg-primary-powder mb-10 flex w-full max-w-182.5 flex-col items-center gap-4 rounded-lg p-5 sm:flex-row sm:gap-6">
        <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-white">
          <PhoneIcon className="h-5 w-auto shrink-0" />
        </div>
        <div className="flex flex-1 flex-col gap-1 text-center sm:text-left">
          <span className="text-neutral-black text-sm font-bold">{t("areYouAnInfluencer")}</span>
          <span className="text-neutral-grey-dark text-sm">{t("influencer_app_band_description")}</span>
        </div>
        <Button
          buttonStyle="White"
          className="h-auto shrink-0 gap-2 px-4 py-3 text-sm"
          dataTestId="registration-influencer-app-band-button"
          onClick={openAppRedirect}>
          {t("download_the_app")}
          <RightArrowIcon />
        </Button>
      </div>
    </div>
  );
}
